/* eslint-disable @typescript-eslint/naming-convention */
import { createTheme as createMuiTheme } from '@material-ui/core/styles'
import { unstable_createMuiStrictModeTheme } from '@material-ui/core'
import { colors as bsColors } from '@resiliatech/birdsong'
import { colors } from './Colors'

/* unstable_createMuiStrictModeTheme Generates a theme that reduces the amount of warnings 
 inside React.StrictMode eg "Warning: findDOMNode is deprecated in StrictMode."" 
 See https://v4.mui.com/customization/theming/#unstable-createmuistrictmodetheme-options-args-theme
 This would not be needed after upgrading to MUI v5
 */
const createTheme =
  process.env.NODE_ENV === 'production'
    ? createMuiTheme
    : unstable_createMuiStrictModeTheme

export const baseTheme = createTheme({
  palette: {
    type: 'light',
    common: {
      black: colors.black,
      white: colors.resiliaWhite,
    },
    primary: {
      main: colors.resiliaBlue,
      dark: colors.navy,
      contrastText: colors.resiliaWhite,
      light: colors.lightestGrey,
    },
    secondary: {
      main: colors.resiliaGreen,
      dark: colors.green,
    },
    error: {
      main: colors.red,
      light: colors.coral,
    },
    warning: {
      main: colors.yellow,
      light: colors.lightYellow,
    },
    info: {
      main: colors.blue,
      light: colors.lightBlue,
      dark: colors.darkBlue,
    },
    text: {
      primary: bsColors.copy.paragraphDark,
      secondary: colors.darkGrey,
    },
    background: {
      default: colors.backgroundGrey,
    },
  },
  typography: {
    fontSize: 16,
    h1: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '48px',
      lineHeight: '115%',
    },
    h2: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '40px',
      lineHeight: '125%',
    },
    h3: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '32px',
      lineHeight: '125%',
    },
    h4: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '24px',
      lineHeight: '135%',
    },
    h5: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '20px',
      lineHeight: '135%',
    },
    h6: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '16px',
      lineHeight: '135%',
    },
    body1: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '14px',
      lineHeight: '165%',
    },
    body2: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '12.8px',
      lineHeight: '165%',
    },
    subtitle1: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '20px',
      lineHeight: '135%',
      fontWeight: 600,
    },
    button: {
      fontFamily: '"Source Sans Pro", sans-serif',
    },
  },
  overrides: {
    MuiListItem: {
      root: {
        '&$selected': {
          backgroundColor: colors.navy,
          borderLeft: `8px solid ${colors.green}`,
          paddingLeft: '8px',
        },
      },
      gutters: {
        paddingRight: '5px',
      },
    },
    MuiStepLabel: {
      label: {
        fontWeight: 700,
        color: colors.darkGrey,
        '&$active': {
          fontWeight: 700,
          color: colors.darkGrey,
        },
        '&$completed': {
          fontWeight: 700,
          color: colors.darkGrey,
        },
      },
    },
    MuiButton: {
      root: {
        fontWeight: 600,
        textTransform: 'none',
        fontSize: 14,
        borderRadius: 4,
        color: colors.resiliaBlue,
      },
      outlined: {
        border: `2px solid ${colors.resiliaBlue}`,
      },
    },
    MuiInputLabel: {
      outlined: {
        backgroundColor: colors.resiliaWhite,
        paddingLeft: 2,
        paddingRight: 2,
      },
    },
    MuiChip: {
      outlined: {
        backgroundColor: colors.lightestBlue,
        minWidth: '40px',
        marginRight: '10px',
        border: `1px solid ${colors.lightBlue}`,
      },
      root: {
        fontFamily: '"Source Sans Pro", sans-serif',
      },
      deletable: {
        backgroundColor: colors.lightestBlue,
        minWidth: '40px',
        marginRight: '10px',
        border: `1px solid ${colors.lightBlue}`,
      },
      deleteIcon: {
        color: colors.resiliaBlue,
      },
    },

    MuiTableCell: {
      head: {
        fontFamily: 'Source Sans Pro',
        fontSize: 13,
        fontWeight: 700,
        lineHeight: '21px',
      },
    },
    MuiTextField: {
      root: {
        '& input[type=number]': {
          '-moz-appearance': 'textfield',
        },
        '& input[type=number]::-webkit-outer-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0,
        },
        '& input[type=number]::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0,
        },
      },
    },
    MuiCssBaseline: {
      '@global': {
        main: {
          overflowX: 'hidden',
        },
        body: {
          backgroundColor: bsColors.background.primaryBackground,
        },
      },
    },
  },
})
